import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiAlertTriangle, FiTrash2, FiCheck } = FiIcons;

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm", 
  cancelText = "Cancel",
  type = "warning", // warning, danger, success, info
  icon,
  isLoading = false
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-danger-100',
          iconColor: 'text-danger-600',
          confirmButton: 'bg-danger-600 hover:bg-danger-700 focus:ring-danger-500',
          defaultIcon: FiTrash2
        };
      case 'success':
        return {
          iconBg: 'bg-success-100',
          iconColor: 'text-success-600',
          confirmButton: 'bg-success-600 hover:bg-success-700 focus:ring-success-500',
          defaultIcon: FiCheck
        };
      case 'info':
        return {
          iconBg: 'bg-primary-100',
          iconColor: 'text-primary-600',
          confirmButton: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
          defaultIcon: FiAlertTriangle
        };
      default: // warning
        return {
          iconBg: 'bg-warning-100',
          iconColor: 'text-warning-600',
          confirmButton: 'bg-warning-600 hover:bg-warning-700 focus:ring-warning-500',
          defaultIcon: FiAlertTriangle
        };
    }
  };

  const styles = getTypeStyles();
  const IconComponent = icon || styles.defaultIcon;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full ${styles.iconBg} flex items-center justify-center`}>
                <SafeIcon icon={IconComponent} className={`w-5 h-5 ${styles.iconColor}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 leading-relaxed">
              {message}
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 p-6 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`flex-1 px-4 py-2 text-white rounded-lg focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 ${styles.confirmButton}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConfirmationModal;